import ky from "ky-universal";
import { useEffect, useLayoutEffect, useState } from "react";
import { buildSearchEngine, SearchEngine } from "@coveo/headless";
import {
	CoveoEngineProps,
	CoveoLayouts
} from "components/coveo-headless/types/coveo-headless-types";
import DotLoader from "components/utility/dot-loader/DotLoader";
import CoveoSearch from "components/coveo-headless/CoveoSearch";
import InsightsLoader from "components/coveo-headless/coveo-loaders/InsightsLoader/InsightsLoader";
import Portal from "components/utility/portal/Portal";
import { SearchPageResultsPlaceholderPage } from "components/search/search-results-placeholders/SearchPageResultsPlaceholderPage";
import { attachCoveoUA, findPageInformationObject } from "./coveoUAHelpers";
import { PublicSiteResultsPlaceholderPage } from "components/search/search-results-placeholders/PublicSiteResultsPlaceholderPage";
import ClientHomeLoader from "components/coveo-headless/coveo-loaders/ClientHomeLoader/ClientHomeLoader";

type Token = {
	token: string;
};

// Importing Coveo Analytics as a standalone component as shown in the docs results in an error. This is the fallback solution:
const CoveoEngine = ({ data, dataset, pageType }: CoveoEngineProps) => {
	const { uniqueid } = dataset;
	const { cq, filters, layout, templates } = data;
	const { numberOfResults, pipeline, searchHub, sortCriteria } = filters;
	const { apiBaseUrl, coveoPlatformEndpoint, coveoTokenEndpoint, organizationId } =
		window.coveoSearchSettings;

	//engine
	const [coveoEngine, setCoveoEngine] = useState<SearchEngine>();
	const [beartoken, setToken] = useState<string>("");
	const [isCoveoUAAttached, setIsCoveoUAAttached] = useState(false);

	//Used to set default sort for results on non search pages
	const sortActive = sortCriteria.filter((item) => item.isActive === true);

	// Set for pages that do not have sort by functionality
	const pagesWithoutSortBy = new Set<string | undefined>([
		CoveoLayouts.Newsroom,
		CoveoLayouts.Insights
	]);

	// Removes placeholder cards renderd by cshtml
	useLayoutEffect(() => {
		const placeholder = document.querySelector<HTMLDivElement>(".coveo-search-results-placeholder");

		placeholder?.remove();
	}, []);

	useEffect(() => {
		if (!isCoveoUAAttached) {
			attachCoveoUA(
				window,
				document,
				"script",
				"https://static.cloud.coveo.com/coveo.analytics.js/2/coveoua.js"
			);
			setIsCoveoUAAttached(true);
		}
	}, [isCoveoUAAttached]);

	useEffect(() => {
		async function fetchToken() {
			const response: Token = await ky<Token>(window.location.origin + coveoTokenEndpoint)
				.then((res) => {
					if (res.status === 200) {
						return res.json();
					} else {
						throw Error(`${res.status}`);
					}
				})
				.catch((error) => {
					console.error(error);
					return error;
				});
			setToken(response.token);
		}
		fetchToken();
	}, [coveoTokenEndpoint]);

	useEffect(() => {
		if (beartoken) {
			setCoveoEngine(
				buildSearchEngine({
					configuration: {
						organizationId,
						accessToken: beartoken,
						organizationEndpoints: {
							platform: window.location.origin + coveoPlatformEndpoint,
							analytics: apiBaseUrl
						},
						preprocessRequest: (request, clientOrigin) => {
							if (clientOrigin === "searchApiFetch") {
								const headers = request?.headers ? new Headers(request.headers) : new Headers();
								if (!headers.has("Authorization")) {
									headers.set("Authorization", `Bearer ${beartoken}`);
								}
								request.headers = headers;
								const body = JSON.parse(request.body as string);
								body.pipeline = pipeline;
								body.numberOfResults = numberOfResults;
								// insights and newsroom DO NOT have sortby filters
								// therefore sort must be sent in initial request
								// for search pages it has to be set on change within the searchParameterManager
								if (pagesWithoutSortBy.has(layout)) {
									body.sortCriteria = sortActive[0].criteria;
								}
								body.cq = cq;
								request.body = JSON.stringify(body);
							}
							return request;
						}
					}
				})
			);

			const pageInfomation = findPageInformationObject(window.dataLayer);
			if (pageInfomation) {
				window.coveoua("init", beartoken, apiBaseUrl);
				window.coveoua("send", "view", {
					contentIdKey: "@permanentid",
					contentIdValue: pageInfomation.shortId,
					customData: {
						context_userType: pageInfomation.audience,
						context_userCountry: pageInfomation.country,
						context_userEnv: pageInfomation.env
					}
				});
			}
		}
		// adding all dependencies react recommends cause coveo to go into an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiBaseUrl, beartoken, coveoPlatformEndpoint, cq, numberOfResults, organizationId, pipeline]);

	if (coveoEngine === undefined) {
		// use the prop layout to identify the page
		switch (layout) {
			case CoveoLayouts.Insights:
				return (
					<Portal el={".coveo-search-results"}>
						<InsightsLoader numberOfCards={Number(numberOfResults)} />
					</Portal>
				);
			case CoveoLayouts.Newsroom:
				return (
					<Portal el={".coveo-search-results"}>
						<div className="media-center-loader">
							<div className="media-center-loader-container">
								<div className="grid">
									<DotLoader />
								</div>
							</div>
						</div>
					</Portal>
				);
			case CoveoLayouts.PublicSearchResults:
				return (
					<Portal el={".coveo-search-results"}>
						<PublicSiteResultsPlaceholderPage numberOfCards={Number(numberOfResults)} />
					</Portal>
				);
			case CoveoLayouts.ClientBrowse:
				return (
					<Portal el={".coveo-search-results"}>
						<ClientHomeLoader numberOfCards={Number(filters.numberOfResults)} />
					</Portal>
				);
			case CoveoLayouts.ClientSearchResults:
				return (
					<Portal el={".coveo-search-results"}>
						<SearchPageResultsPlaceholderPage numberOfCards={Number(numberOfResults)} />
					</Portal>
				);
			// if the layout prop is not available it is most likely the standalone public site search box
			default:
				return null;
		}
	}

	return (
		<CoveoSearch
			engine={coveoEngine!}
			jsonData={data}
			layout={layout}
			pageType={pageType}
			searchHub={searchHub}
			templates={templates}
			uniqueId={uniqueid}
		/>
	);
};

export default CoveoEngine;
